import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Users, Target, Activity, ChevronRight } from "lucide-react";
import { API_URL } from "../../components/config";

const FunnelStats = () => {
  const [funnelData, setFunnelData] = useState({
    stages: [],
    conversionRates: [],
    totalUsers: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFunnelData = async () => {
      try {
        setLoading(true);
        
        // Fetch funnel statistics (you'll need to create this endpoint)
        const response = await fetch(`${API_URL}/application_statistics/funnel-stats`);
        const data = await response.json();
        
        // Fallback data if API doesn't exist yet
        const fallbackData = {
          stages: [
            { name: "App Downloads", count: 1000, percentage: 100, color: "blue" },
            { name: "Account Registration", count: 750, percentage: 75, color: "green" },
            { name: "Profile Completion", count: 600, percentage: 60, color: "yellow" },
            { name: "First Ride Request", count: 450, percentage: 45, color: "purple" },
            { name: "Completed Rides", count: 300, percentage: 30, color: "pink" },
            { name: "Repeat Users", count: 180, percentage: 18, color: "indigo" }
          ],
          conversionRates: [
            { from: "App Downloads", to: "Account Registration", rate: 75 },
            { from: "Account Registration", to: "Profile Completion", rate: 80 },
            { from: "Profile Completion", to: "First Ride Request", rate: 75 },
            { from: "First Ride Request", to: "Completed Rides", rate: 67 },
            { from: "Completed Rides", to: "Repeat Users", rate: 60 }
          ],
          totalUsers: 1000
        };
        
        setFunnelData(data || fallbackData);
      } catch (error) {
        console.error("Error fetching funnel stats:", error);
        // Use fallback data on error
        setFunnelData({
          stages: [
            { name: "App Downloads", count: 1000, percentage: 100, color: "blue" },
            { name: "Account Registration", count: 750, percentage: 75, color: "green" },
            { name: "Profile Completion", count: 600, percentage: 60, color: "yellow" },
            { name: "First Ride Request", count: 450, percentage: 45, color: "purple" },
            { name: "Completed Rides", count: 300, percentage: 30, color: "pink" },
            { name: "Repeat Users", count: 180, percentage: 18, color: "indigo" }
          ],
          conversionRates: [
            { from: "App Downloads", to: "Account Registration", rate: 75 },
            { from: "Account Registration", to: "Profile Completion", rate: 80 },
            { from: "Profile Completion", to: "First Ride Request", rate: 75 },
            { from: "First Ride Request", to: "Completed Rides", rate: 67 },
            { from: "Completed Rides", to: "Repeat Users", rate: 60 }
          ],
          totalUsers: 1000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFunnelData();
  }, []);

  const FunnelStage = ({ stage, index, totalStages }) => (
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative"
    >
      {/* Connection Line */}
      {index < totalStages - 1 && (
        <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0.5 h-8 bg-gray-300 z-0" />
      )}
      
      {/* Chevron */}
      {index < totalStages - 1 && (
        <div className="absolute left-1/2 transform -translate-x-1/2 top-full z-10">
          <ChevronRight className="w-6 h-6 text-gray-400" />
        </div>
      )}
      
      {/* Stage Card */}
      <div className="relative bg-white rounded-xl shadow-lg p-6 border border-gray-100 z-20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">{stage.name}</h3>
          <div className={`w-3 h-3 rounded-full bg-${stage.color}-500`} />
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold text-gray-900">{stage.count}</span>
            <span className="text-sm text-gray-500">{stage.percentage}%</span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className={`bg-gradient-to-r from-${stage.color}-400 to-${stage.color}-600 h-2 rounded-full`}
              initial={{ width: 0 }}
              animate={{ width: `${stage.percentage}%` }}
              transition={{ duration: 1, delay: index * 0.2 }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );

  const ConversionRate = ({ rate, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      className="bg-white rounded-lg shadow-md p-4 border border-gray-100"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-700">{rate.from}</p>
          <p className="text-xs text-gray-500">→ {rate.to}</p>
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold ${rate.rate >= 70 ? 'text-green-600' : rate.rate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
            {rate.rate}%
          </div>
          <p className="text-xs text-gray-500">conversion</p>
        </div>
      </div>
    </motion.div>
  );

  const SummaryCard = ({ title, value, icon, color, subtitle }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-6 border border-gray-100"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          {icon}
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-800">{value}</h3>
      <p className="text-sm text-gray-500 mt-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </motion.div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center space-x-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="w-48">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
                  <div className="h-8 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <Target className="w-6 h-6 mr-2 text-purple-600" />
          Funnel Analytics
        </h2>
        <div className="text-sm text-gray-500">
          Total Users: {funnelData.totalUsers.toLocaleString()}
        </div>
      </div>

      {/* Funnel Stages */}
      <div className="flex justify-center space-x-4 overflow-x-auto pb-4">
        {funnelData.stages.map((stage, index) => (
          <div key={index} className="flex-shrink-0 w-64">
            <FunnelStage 
              stage={stage} 
              index={index} 
              totalStages={funnelData.stages.length} 
            />
          </div>
        ))}
      </div>

      {/* Conversion Rates */}
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
          Conversion Rates
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {funnelData.conversionRates.map((rate, index) => (
            <ConversionRate key={index} rate={rate} index={index} />
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <SummaryCard
          title="Overall Conversion"
          value={`${Math.round((funnelData.stages[funnelData.stages.length - 1].count / funnelData.stages[0].count) * 100)}%`}
          icon={<Target className="w-5 h-5 text-purple-600" />}
          color="purple"
          subtitle="From download to repeat"
        />
        <SummaryCard
          title="Best Conversion"
          value={`${Math.max(...funnelData.conversionRates.map(r => r.rate))}%`}
          icon={<TrendingUp className="w-5 h-5 text-green-600" />}
          color="green"
          subtitle="Highest stage conversion"
        />
        <SummaryCard
          title="Drop-off Point"
          value={funnelData.conversionRates.reduce((min, rate) => rate.rate < min.rate ? rate : min).from}
          icon={<Activity className="w-5 h-5 text-red-600" />}
          color="red"
          subtitle="Lowest conversion rate"
        />
        <SummaryCard
          title="Active Users"
          value={funnelData.stages[funnelData.stages.length - 1].count.toLocaleString()}
          icon={<Users className="w-5 h-5 text-blue-600" />}
          color="blue"
          subtitle="Completed funnel"
        />
      </div>
    </div>
  );
};

export default FunnelStats;
